import {AfterViewInit, ChangeDetectorRef, Component, OnInit} from "@angular/core";
import {Router} from "@angular/router";
import {Article} from '../shared/models/article.interface';
import {ConstructedProfile, ProfileShape} from "../model-viewer/profile-shape-object";
import * as THREE from "three";
import {
  ArticleListDataModel,
  ArticleModel, EngineType,
  EventModel,
  InputEventKey,
  ShapeModel,
  USC_LIB_CONFIG
} from "@schuco-vcl/usc-lib";
import {ShapeService} from "../model-viewer/shape.service";
import {forkJoin} from "rxjs";

@Component({
  selector: 'app-article-viewer',
  templateUrl: './article-viewer.component.html',
  styleUrls: ['./article-viewer.component.scss'],
  standalone: false,
  providers: [
    {
      provide: USC_LIB_CONFIG,
      useValue: {
        engineType: EngineType.Article,
      }
    }
  ],
})
export class ArticleViewerComponent implements OnInit, AfterViewInit {
  articles: Article[] = [];
  jsUrlToPassIn: string[] = [];
  canvasInputData: EventModel | null = null;
  objData = new Map<string, string>();
  isLoading: boolean = true;
  hasError: boolean = false;
  errorMessage: string = 'Something went wrong while loading the model';
  errorStack = '';
  debugMode: boolean = false;

  constructor(
    private router: Router, 
    private shapeService: ShapeService, 
    private cdr: ChangeDetectorRef
  ) {
    this.debugMode = window.location.search.includes('debug=true');
    
    const navigation = this.router.getCurrentNavigation();
    const stateData = navigation?.extras.state?.['data'];
    if (stateData) {
      this.jsUrlToPassIn = stateData;
    } else {
      const storedData = sessionStorage.getItem('articleViewerData');
      if (storedData) {
        this.jsUrlToPassIn = JSON.parse(storedData);
        sessionStorage.removeItem('articleViewerData');
      }
    }
  }

  ngOnInit() {
    // Check for global USC library error first (highest priority)
    const globalUSCError = (window as any).__GLOBAL_USC_ERROR__;
    if (globalUSCError) {
      console.log('Global USC Library error detected:', globalUSCError);
      this.showError(globalUSCError.message);
      return;
    }
    
    // Check for general startup errors
    const startupError = (window as any).__APP_RUNTIME_ERROR__;
    if (startupError) {
      console.log('General startup error detected:', startupError);
      this.showError(startupError);
      return;
    }
  }

  ngAfterViewInit() {
    console.log('ArticleViewer: ngAfterViewInit called, setting up comprehensive error detection');
    
    // Add immediate error detection
    this.setupImmediateErrorDetection();
    
    // Add multiple error detection mechanisms for new tab scenario
    this.setupErrorDetection();
    
    // Start aggressive error monitoring
    this.startErrorMonitoring();
    
    setTimeout(() => {
      if (this.isLoading && !this.hasError && !this.canvasInputData) {
        console.warn('ArticleViewer: Still loading after timeout, checking for errors...');
        this.checkForErrors();
      }
    }, 15000);

    this.getArticles();
  }

  private setupImmediateErrorDetection() {
    // Override console.error immediately to catch all errors
    const originalConsoleError = console.error;
    const self = this;
    
    console.error = function(...args: any[]) {
      const errorMessage = args.join(' ');
      console.log('Console.error intercepted:', errorMessage);
      
      // Check for USC library error patterns
      if (errorMessage.includes('Cannot read properties of undefined (reading \'forEach\')') ||
          errorMessage.includes('Cannot read properties of undefined (reading "forEach")') ||
          errorMessage.includes('schuco-vcl-usc-lib.mjs:10472') ||
          (errorMessage.includes('forEach') && errorMessage.includes('Cannot read'))) {
        
        console.log('USC Library forEach error detected via console override!');
        
        // Display error immediately
        setTimeout(() => {
          if (!self.hasError) {
            self.showError(`USC Library Error: Cannot read properties of undefined (reading 'forEach'). This occurs when the article data structure is incompatible with the 3D viewer.`);
          }
        }, 0);
        
        // Store for global detection
        (window as any).__GLOBAL_USC_ERROR__ = {
          message: `USC Library Error: Cannot read properties of undefined (reading 'forEach'). This occurs when the article data structure is incompatible with the 3D viewer.`,
          originalError: errorMessage,
          timestamp: Date.now()
        };
      }
      
      // Call original console.error
      originalConsoleError.apply(console, args);
    };
    
    // Also override console.warn for additional detection
    const originalConsoleWarn = console.warn;
    console.warn = function(...args: any[]) {
      const errorMessage = args.join(' ');
      
      if (errorMessage.includes('Cannot read properties of undefined (reading \'forEach\')') ||
          errorMessage.includes('Cannot read properties of undefined (reading "forEach")') ||
          errorMessage.includes('schuco-vcl-usc-lib.mjs:10472')) {
        
        console.log('USC Library forEach error detected via console.warn!');
        
        setTimeout(() => {
          if (!self.hasError) {
            self.showError(`USC Library Error: Cannot read properties of undefined (reading 'forEach'). This occurs when the article data structure is incompatible with the 3D viewer.`);
          }
        }, 0);
      }
      
      originalConsoleWarn.apply(console, args);
    };
  }

  private startErrorMonitoring() {
    // Poll for errors every 500ms for the first 10 seconds
    let pollCount = 0;
    const maxPolls = 20; // 20 * 500ms = 10 seconds
    
    const pollForErrors = () => {
      pollCount++;
      
      // Check for USC library error in multiple ways
      this.checkForUSCLibraryError();
      
      // Check if we have data but no canvas (indicates processing error)
      if (this.objData.size > 0 && !this.canvasInputData && !this.hasError && pollCount > 2) {
        console.warn('Detected data loaded but no canvas - likely USC library error');
        this.showError(`USC Library Error: Cannot read properties of undefined (reading 'forEach'). This occurs when the article data structure is incompatible with the 3D viewer.`);
        return;
      }
      
      if (pollCount < maxPolls && !this.hasError) {
        setTimeout(pollForErrors, 500);
      }
    };
    
    // Start polling after a short delay
    setTimeout(pollForErrors, 1000);
  }

  private checkForUSCLibraryError() {
    // Check various sources for the USC library error
    
    // 1. Check for global USC error (highest priority - from main.ts)
    const globalUSCError = (window as any).__GLOBAL_USC_ERROR__;
    if (globalUSCError) {
      console.log('Global USC Library error found:', globalUSCError);
      this.showError(globalUSCError.message);
      return;
    }
    
    // 2. Check global error storage
    const globalError = (window as any).__ARTICLE_VIEWER_ERROR__ || (window as any).__APP_RUNTIME_ERROR__;
    if (globalError) {
      const errorMessage = globalError?.message || globalError || '';
      const errorStack = globalError?.stack || '';
      
      if (this.isUSCLibraryForEachError(errorMessage, errorStack)) {
        this.showError(`USC Library Error: Cannot read properties of undefined (reading 'forEach'). This occurs when the article data structure is incompatible with the 3D viewer.`);
        return;
      }
    }
    
    // 3. Check if there are any recent console errors (by checking console state)
    try {
      // Try to access console logs if possible
      const consoleLogs = (console as any).logs || [];
      for (const log of consoleLogs) {
        if (typeof log === 'string' && this.isUSCLibraryForEachError(log, '')) {
          this.showError(`USC Library Error: Cannot read properties of undefined (reading 'forEach'). This occurs when the article data structure is incompatible with the 3D viewer.`);
          return;
        }
      }
    } catch (e) {
      // Ignore if we can't access console logs
    }
    
    // 4. Check for error patterns in window object
    const windowError = (window as any).lastError || (window as any).error;
    if (windowError) {
      const errorMessage = windowError?.message || windowError || '';
      const errorStack = windowError?.stack || '';
      
      if (this.isUSCLibraryForEachError(errorMessage, errorStack)) {
        this.showError(`USC Library Error: Cannot read properties of undefined (reading 'forEach'). This occurs when the article data structure is incompatible with the 3D viewer.`);
        return;
      }
    }
  }

  private isUSCLibraryForEachError(errorMessage: string, errorStack: string): boolean {
    if (!errorMessage) return false;
    
    return (
      errorMessage.includes('Cannot read properties of undefined (reading \'forEach\')') ||
      errorMessage.includes('Cannot read properties of undefined (reading "forEach")') ||
      (errorMessage.includes('forEach') && errorStack.includes('schuco-vcl-usc-lib.mjs:10472')) ||
      (errorMessage.includes('forEach') && errorStack.includes('schuco-vcl-usc-lib.mjs')) ||
      errorMessage.includes('schuco-vcl-usc-lib.mjs:10472')
    );
  }

  private setupErrorDetection() {
    // Set up global error handlers to catch errors in new tab
    window.addEventListener('error', (event: ErrorEvent) => {
      console.error('Global error caught in ArticleViewer:', event.error);
      const errorMessage = event.error?.message || event.message || 'Unknown error occurred';
      const errorStack = event.error?.stack || '';
      
      // Check for the specific USC library forEach error
      if (errorMessage.includes('Cannot read properties of undefined (reading \'forEach\')') ||
          errorMessage.includes('Cannot read properties of undefined (reading "forEach")') ||
          (errorMessage.includes('forEach') && errorStack.includes('schuco-vcl-usc-lib.mjs:10472'))) {
        this.showError(`USC Library Error: Cannot read properties of undefined (reading 'forEach'). This occurs when the article data structure is incompatible with the 3D viewer.`);
        return;
      }
      
      // Check for other forEach errors
      if (errorMessage.includes('forEach') || errorMessage.includes('Cannot read')) {
        this.showError(`JavaScript execution error: ${errorMessage}`);
        return;
      }
      
      // Store error for detection
      (window as any).__ARTICLE_VIEWER_ERROR__ = event.error;
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection in ArticleViewer:', event.reason);
      const errorMessage = event.reason?.message || event.reason || 'Promise rejection occurred';
      const errorStack = event.reason?.stack || '';
      
      // Check for the specific USC library forEach error
      if (errorMessage.includes('Cannot read properties of undefined (reading \'forEach\')') ||
          errorMessage.includes('Cannot read properties of undefined (reading "forEach")') ||
          (errorMessage.includes('forEach') && errorStack.includes('schuco-vcl-usc-lib.mjs:10472'))) {
        this.showError(`USC Library Error: Cannot read properties of undefined (reading 'forEach'). This occurs when the article data structure is incompatible with the 3D viewer.`);
        return;
      }
      
      // Check for other forEach errors
      if (errorMessage.includes('forEach') || errorMessage.includes('Cannot read')) {
        this.showError(`JavaScript execution error: ${errorMessage}`);
        return;
      }
      
      // Store error for detection
      (window as any).__ARTICLE_VIEWER_ERROR__ = event.reason;
    });

    // Check for existing errors that might have occurred before this setup
    const existingError = (window as any).__ARTICLE_VIEWER_ERROR__ || (window as any).__APP_RUNTIME_ERROR__;
    if (existingError) {
      console.log('Found existing error on setup:', existingError);
      const errorMessage = existingError?.message || existingError || 'Existing error detected';
      const errorStack = existingError?.stack || '';
      
      // Check for the specific USC library forEach error
      if (errorMessage.includes('Cannot read properties of undefined (reading \'forEach\')') ||
          errorMessage.includes('Cannot read properties of undefined (reading "forEach")') ||
          (errorMessage.includes('forEach') && errorStack.includes('schuco-vcl-usc-lib.mjs:10472'))) {
        this.showError(`USC Library Error: Cannot read properties of undefined (reading 'forEach'). This occurs when the article data structure is incompatible with the 3D viewer.`);
        return;
      }
      
      if (errorMessage.includes('forEach') || errorMessage.includes('Cannot read')) {
        this.showError(`JavaScript execution error: ${errorMessage}`);
      }
    }
  }

  private checkForErrors() {
    // Multiple error checking strategies
    
    // 1. Check global error storage
    const globalError = (window as any).__ARTICLE_VIEWER_ERROR__ || (window as any).__APP_RUNTIME_ERROR__;
    if (globalError) {
      const errorMessage = globalError?.message || globalError || 'Global error detected';
      const errorStack = globalError?.stack || '';
      
      // Check for the specific USC library forEach error
      if (errorMessage.includes('Cannot read properties of undefined (reading \'forEach\')') ||
          errorMessage.includes('Cannot read properties of undefined (reading "forEach")') ||
          (errorMessage.includes('forEach') && errorStack.includes('schuco-vcl-usc-lib.mjs:10472'))) {
        this.showError(`USC Library Error: Cannot read properties of undefined (reading 'forEach'). This occurs when the article data structure is incompatible with the 3D viewer.`);
        return;
      }
      
      if (errorMessage.includes('forEach') || errorMessage.includes('Cannot read')) {
        this.showError(`JavaScript execution error: ${errorMessage}`);
        return;
      }
    }

    // 2. Check console errors (if possible)
    try {
      // Try to detect if there are any console errors
      const originalError = console.error;
      let errorDetected = false;
      const self = this; // Preserve 'this' context
      
      console.error = function(...args: any[]) {
        errorDetected = true;
        const errorMessage = args.join(' ');
        
        // Check for the specific USC library forEach error
        if (errorMessage.includes('Cannot read properties of undefined (reading \'forEach\')') ||
            errorMessage.includes('Cannot read properties of undefined (reading "forEach")') ||
            errorMessage.includes('schuco-vcl-usc-lib.mjs:10472')) {
          setTimeout(() => {
            if (!self.hasError) {
              self.showError(`USC Library Error: Cannot read properties of undefined (reading 'forEach'). This occurs when the article data structure is incompatible with the 3D viewer.`);
            }
          }, 0);
        }
        // Check for other forEach errors
        else if (errorMessage.includes('forEach') || errorMessage.includes('Cannot read')) {
          setTimeout(() => {
            if (!self.hasError) {
              self.showError(`JavaScript execution error: ${errorMessage}`);
            }
          }, 0);
        }
        
        originalError.apply(console, args);
      };
      
      // Restore original error after a delay
      setTimeout(() => {
        console.error = originalError;
        if (!errorDetected && !this.canvasInputData) {
          this.showError('Failed to load article models. The article data may be corrupted or incompatible.');
        }
      }, 2000);
      
    } catch (e) {
      console.warn('Could not set up console error detection:', e);
    }

    // 3. Check if we have valid data but no canvas
    if (this.objData.size > 0 && !this.canvasInputData && !this.hasError) {
      this.showError('Article data was loaded but failed to display. The article format may be incompatible.');
    }
  }

  getArticles() {
    console.log('getArticles called with URLs:', this.jsUrlToPassIn);
    
    setTimeout(() => {
      if (!this.jsUrlToPassIn || this.jsUrlToPassIn.length === 0) {
        console.error('No article URLs provided');
        this.showError('No article data found. Please select articles to view.');
        return;
      }

      this.isLoading = true;
      this.hasError = false;
      this.cdr.detectChanges();

      const observables = this.jsUrlToPassIn.map(url => {
        console.log('Creating observable for URL:', url);
        return this.shapeService.getRemoteJs(url);
      });

      console.log('Starting forkJoin with', observables.length, 'observables');

      forkJoin(observables).subscribe({
        next: (results) => {
          console.log('forkJoin completed successfully with results:', results);
          
          if (!results || results.length === 0) {
            console.error('No results returned from forkJoin');
            this.showError('No article data found');
            return;
          }

          const validResults = results.filter(result => result && result.trim() !== '');
          if (validResults.length === 0) {
            console.error('All results are empty or invalid');
            this.showError('All article files are empty or invalid');
            return;
          }

          try {
            this.objData.clear();
            results.forEach((result, index) => {
              const url = this.jsUrlToPassIn[index];
              const key = url.split('/').pop() || `article_${index}`;
              this.objData.set(key, result);
              console.log(`Stored JS data for key ${key}, length: ${result.length}`);
            });

            console.log('Processing articles with sendArticlesToViewer...');
            this.sendArticlesToViewer();
            
            this.isLoading = false;
            this.cdr.detectChanges();
            
          } catch (error) {
            console.error('Error processing article data:', error);
            this.showError(error);
          }
        },
        error: (err: any) => {
          console.error('Error loading articles:', err);
          const errorMessage = err?.message || err?.statusText || 'Failed to load article models. Please check your internet connection and try again.';
          this.showError(errorMessage);
        }
      });
    }, 0);
  }

  testForEachError() {
    console.log('Testing forEach error...');
    try {
      const undefinedArray: any = undefined;
      undefinedArray.forEach(() => {});
    } catch (error) {
      console.log('Caught forEach error:', error);
      this.showError(error);
    }
  }

  testUSCError() {
    console.log('Testing USC Library error detection...');
    
    // Simulate the exact USC library error
    const uscError = new Error("Cannot read properties of undefined (reading 'forEach')");
    uscError.stack = "TypeError: Cannot read properties of undefined (reading 'forEach')\n    at schuco-vcl-usc-lib.mjs:10472:29\n    at timer (zone.js:1809:37)";
    
    // Store it as if it was caught by global handler
    (window as any).__GLOBAL_USC_ERROR__ = {
      message: `USC Library Error: Cannot read properties of undefined (reading 'forEach'). This occurs when the article data structure is incompatible with the 3D viewer.`,
      originalError: uscError,
      timestamp: Date.now()
    };
    
    // Also trigger console.error to test console override
    console.error("TypeError: Cannot read properties of undefined (reading 'forEach') at schuco-vcl-usc-lib.mjs:10472:29");
    
    // Show the error
    this.showError(`USC Library Error: Cannot read properties of undefined (reading 'forEach'). This occurs when the article data structure is incompatible with the 3D viewer.`);
    
    console.log('USC Library error test completed. Check if error is displayed in UI.');
  }

  sendArticlesToViewer() {
    try {
      console.log('sendArticlesToViewer: Starting to process articles');
      console.log('sendArticlesToViewer: objData entries:', this.objData.size);
      
      const articles: ArticleModel[] = [];
      const articleDataList: any[] = []; // Store original data for DrArticleModel conversion
      
      this.objData.forEach((value: string, key: string) => {
        console.log(`sendArticlesToViewer: Processing article ${key}:`, value?.substring(0, 100) + '...');
        
        try {
          let articleObject;
          try {
            articleObject = this.getArticleObject(value, key);
          } catch (execError) {
            console.warn(`sendArticlesToViewer: Could not execute JS for ${key}, trying alternative approach:`, execError);
            articleObject = {
              articleName: key,
              system: 'Default',
              type: 'Default',
              depth: 1,
              width: 1,
              insideWidth: 1,
              outsideWidth: 1,
              shapes: []
            };
          }
          
          console.log('sendArticlesToViewer: Got article object:', articleObject);
          
          if (!articleObject) {
            console.warn(`sendArticlesToViewer: Article object is undefined for key ${key}, skipping`);
            return;
          }
          
          // Store the original articleObject data for later conversion
          articleDataList.push(articleObject);
          
          const shapes: ShapeModel[] = [];
          if (articleObject.shapes && Array.isArray(articleObject.shapes)) {
            console.log(`sendArticlesToViewer: Processing ${articleObject.shapes.length} shapes for ${key}`);
            
            articleObject.shapes.forEach((shape: any, shapeIndex: number) => {
              try {
                if (shape) {
                  const shapeModel = ShapeModel.createObjFromShape(shape, shape.useMaterial);
                  shapes.push(shapeModel);
                } else {
                  console.warn(`sendArticlesToViewer: Shape at index ${shapeIndex} is null/undefined for ${key}`);
                }
              } catch (shapeError) {
                console.warn(`sendArticlesToViewer: Error processing shape ${shapeIndex} for ${key}:`, shapeError);
              }
            });
          } else {
            console.warn(`sendArticlesToViewer: No valid shapes array found for ${key}, creating empty shapes`);
          }
          
          try {
            const article = ArticleModel.createObj(
              articleObject.articleName || key, 
              articleObject.system || 'Default', 
              articleObject.type || 'Default',
              articleObject.depth || 1, 
              articleObject.width || 1, 
              articleObject.insideWidth || 1, 
              articleObject.outsideWidth || 1, 
              shapes
            );
            articles.push(article);
            console.log(`sendArticlesToViewer: Successfully created article for ${key}`);
          } catch (articleCreateError) {
            console.error(`sendArticlesToViewer: Error creating article model for ${key}:`, articleCreateError);
          }
          
        } catch (articleError) {
          console.error(`sendArticlesToViewer: Error processing article ${key}:`, articleError);
        }
      });
      
      console.log(`sendArticlesToViewer: Successfully created ${articles.length} articles out of ${this.objData.size} attempts`);
      
      if (articles.length > 0) {
        try {
          // Use the original articleObject data for DrArticleModel conversion
          const drArticles: any[] = articleDataList.map(articleObject => ({
            Code: articleObject.articleName || '',
            Message: '',
            ArticleName: articleObject.articleName || '',
            DrProfile: {
              System: articleObject.system || '',
              Type: articleObject.type || '',
              Depth: articleObject.depth || 0,
              Width: articleObject.width || 0,
              InsideWidth: articleObject.insideWidth || 0,
              OutsideWidth: articleObject.outsideWidth || 0,
              ProfileShapes: articleObject.shapes || []
            },
            CompositeProfileSectionalProperty: null
          }));
          
          // Set up immediate error detection for USC library calls
          const originalError = console.error;
          let uscErrorDetected = false;
          
          console.error = function(...args: any[]) {
            const errorMessage = args.join(' ');
            if (errorMessage.includes('Cannot read properties of undefined (reading \'forEach\')') ||
                errorMessage.includes('Cannot read properties of undefined (reading "forEach")') ||
                errorMessage.includes('schuco-vcl-usc-lib.mjs:10472')) {
              uscErrorDetected = true;
            }
            originalError.apply(console, args);
          };
          
          // Wrap this in a try-catch to specifically catch USC library forEach errors
          try {
            console.log('sendArticlesToViewer: About to call ArticleListDataModel.createDataModel...');
            const eventData = ArticleListDataModel.createDataModel(drArticles);
            this.canvasInputData = EventModel.createEvent(InputEventKey.SetArticles, eventData);
            console.log('sendArticlesToViewer: Canvas input data created successfully with articles');
            
            // Restore console error
            console.error = originalError;
            
          } catch (uscError) {
            // Restore console error
            console.error = originalError;
            
            console.error('USC Library error:', uscError);
            const errorMessage = (uscError as any)?.message || uscError || '';
            
            // Check if it's the specific forEach error from USC library
            if (this.isUSCLibraryForEachError(errorMessage, (uscError as any)?.stack || '') || uscErrorDetected) {
              console.log('USC Library forEach error detected, displaying in UI...');
              this.showError(`USC Library Error: Cannot read properties of undefined (reading 'forEach'). This occurs when the article data structure is incompatible with the 3D viewer.`);
              return;
            } else {
              // For other USC errors, try with empty array
              console.warn('USC Library failed with data, trying empty array:', uscError);
              const fallbackEventData = ArticleListDataModel.createDataModel([]);
              this.canvasInputData = EventModel.createEvent(InputEventKey.SetArticles, fallbackEventData);
              console.log('sendArticlesToViewer: Canvas input data created with empty array as fallback');
            }
          }
          
          // If we get here and no canvas data was created, it might be a silent error
          setTimeout(() => {
            if (!this.canvasInputData && !this.hasError) {
              console.warn('No canvas data created after USC library call - possible silent error');
              this.showError(`USC Library Error: Cannot read properties of undefined (reading 'forEach'). This occurs when the article data structure is incompatible with the 3D viewer.`);
            }
          }, 2000);
          
        } catch (conversionError) {
          console.warn('sendArticlesToViewer: Could not convert to DrArticleModel format, trying empty array:', conversionError);
          const eventData = ArticleListDataModel.createDataModel([]);
          this.canvasInputData = EventModel.createEvent(InputEventKey.SetArticles, eventData);
          console.log('sendArticlesToViewer: Canvas input data created with empty array as fallback');
        }
      } else {
        console.warn('sendArticlesToViewer: No valid articles created, creating empty canvas data');
        const eventData = ArticleListDataModel.createDataModel([]);
        this.canvasInputData = EventModel.createEvent(InputEventKey.SetArticles, eventData);
      }
      
    } catch (error) {
      console.error('sendArticlesToViewer: Critical error in sendArticlesToViewer:', error);
      const errorMessage = (error as any)?.message || '';
      if (this.isUSCLibraryForEachError(errorMessage, (error as any)?.stack || '')) {
        this.showError(`USC Library Error: Cannot read properties of undefined (reading 'forEach'). This occurs when the article data structure is incompatible with the 3D viewer.`);
      } else {
        try {
          const fallbackEventData = ArticleListDataModel.createDataModel([]);
          this.canvasInputData = EventModel.createEvent(InputEventKey.SetArticles, fallbackEventData);
          console.log('sendArticlesToViewer: Created minimal canvas data as fallback');
        } catch (fallbackError) {
          console.error('sendArticlesToViewer: Even fallback failed:', fallbackError);
          this.showError(error);
        }
      }
    }
  }

  private getArticleObject(data: any, keyData: string): any {
    const fixedFnCode = data.replace(keyData, '(THREE) =>');
    const functionFromFile = new Function('return ' + fixedFnCode)();
    return functionFromFile(THREE);
  }

  private showError(error?: any) {
    setTimeout(() => {
      this.isLoading = false;
      this.hasError = true;
      this.canvasInputData = null;

      if (error) {
        if (typeof error === 'string' && error.includes('forEach')) {
          this.errorMessage = `JavaScript execution error: ${error}`;
        } else if (typeof error === 'string' && error.includes('Cannot read')) {
          this.errorMessage = `Data processing error: ${error}`;
        } else if ((error as any)?.message) {
          const message = (error as any).message;
          if (message.includes('forEach')) {
            this.errorMessage = `JavaScript execution error: ${message}`;
          } else if (message.includes('Cannot read')) {
            this.errorMessage = `Data processing error: ${message}`;
          } else {
            this.errorMessage = message;
          }
        } else {
          this.errorMessage = 'Something went wrong while loading the article model';
        }

        this.errorStack =
          (error as any)?.stack ||
          (error as any)?.originalError?.stack ||
          '';
      } else {
        this.errorMessage = 'Something went wrong';
        this.errorStack = '';
      }

      this.cdr.detectChanges();
    }, 0);
  }

  retryLoading() {
    setTimeout(() => {
      this.hasError = false;
      this.errorMessage = '';
      this.errorStack = '';
      this.cdr.detectChanges();
      this.getArticles();
    }, 0);
  }

  closeViewer() {
    window.close();
  }
}
